import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { Eye, EyeOff, ArrowRight, TrendingUp, Building2, Sparkles } from "lucide-react";
import { toast } from "sonner";

const HERO_IMG = "https://images.unsplash.com/photo-1486325212027-8081e485255e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("admin@scitus.ai");
  const [password, setPassword] = useState("Admin@123");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome to Vexen");
      nav("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-white" data-testid="login-page">
      {/* ───────── Left: form panel ───────── */}
      <div className="flex-1 flex flex-col items-center px-6 sm:px-12 lg:px-16 py-10 relative">
        {/* Subtle paper grid */}
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.025]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(15,29,50,1) 1px, transparent 1px), linear-gradient(90deg, rgba(15,29,50,1) 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />

        {/* Centered content stack */}
        <div className="relative z-10 w-full max-w-md flex-1 flex flex-col items-center justify-center">
          {/* Big centered hero logo */}
          <img
            src="/vexen-logo-dark.png"
            alt="Vexen — Realty Intelligence"
            className="mb-10 select-none"
            style={{
              width: "min(420px, 80vw)",
              height: "auto",
              maxHeight: 140,
              objectFit: "contain",
              filter: "drop-shadow(0 8px 32px rgba(15,29,50,0.12))",
            }}
            data-testid="login-logo"
          />

          <div className="inline-flex items-center gap-2 px-2.5 py-1 bg-amber-50 border border-amber-200/60 rounded-full text-[10px] uppercase tracking-[0.2em] text-amber-800 font-mono mb-5">
            <Sparkles size={11} />
            Decision Intelligence
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight font-display text-slate-900 leading-[1.05] text-center">
            Operate with{" "}
            <span style={{
              background: "linear-gradient(135deg, #D4AF37 0%, #B49126 60%, #8E6F1A 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}>conviction.</span>
          </h1>
          <p className="text-sm text-slate-600 mt-3 leading-relaxed text-center max-w-sm">
            Real-time intelligence across sales, collections, inventory, and market posture — for every project in your portfolio.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4 w-full">
            <div>
              <label className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-mono">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 w-full px-3.5 py-3 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-vexen-sidebar focus:ring-2 focus:ring-vexen-sidebar/10 transition-colors"
                placeholder="you@scitus.ai"
                required
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-mono">
                Password
              </label>
              <div className="mt-1.5 relative">
                <input
                  type={show ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3.5 py-3 pr-10 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-vexen-sidebar focus:ring-2 focus:ring-vexen-sidebar/10 transition-colors"
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShow(!show)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                  data-testid="login-password-toggle"
                >
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="group w-full py-3.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-all hover:shadow-lg hover:shadow-vexen-sidebar/20"
              style={{ background: "#0F1D32", color: "white" }}
              data-testid="login-submit-button"
            >
              {loading ? "Signing in..." : "Sign in to Vexen"}
              <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform" />
            </button>
          </form>

          <div className="mt-6 pt-5 border-t border-slate-200 text-[11px] text-slate-500 font-mono leading-relaxed w-full text-center">
            <div className="uppercase tracking-[0.18em] mb-1.5 text-slate-400">Demo credentials</div>
            <div>admin@scitus.ai &nbsp;/&nbsp; Admin@123</div>
            <div>manager@scitus.ai &nbsp;/&nbsp; Manager@123</div>
            <div>viewer@scitus.ai &nbsp;/&nbsp; Viewer@123</div>
          </div>
        </div>

        <div className="relative z-10 text-[10px] text-slate-400 font-mono uppercase tracking-[0.25em] mt-6">
          © {new Date().getFullYear()} Scitus.ai · Vexen Realty Intelligence
        </div>
      </div>

      {/* ───────── Right: hero panel ───────── */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden" style={{ background: "#0F1D32" }}>
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url("${HERO_IMG}")`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "grayscale(0.2) contrast(1.05)",
          }}
        />
        {/* Dark navy gradient overlay */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(160deg, rgba(15,29,50,0.55) 0%, rgba(15,29,50,0.35) 40%, rgba(15,29,50,0.95) 100%)",
          }}
        />
        {/* Gold accent line */}
        <div className="absolute left-12 top-1/2 -translate-y-1/2 w-[1px] h-32" style={{ background: "linear-gradient(180deg, transparent, #D4AF37, transparent)" }} />

        {/* Right side metrics floating cards */}
        <div className="relative z-10 w-full h-full flex flex-col justify-between p-12">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-amber-400/90 font-mono">
            <span className="inline-block w-6 h-[1px] bg-amber-400/60" />
            For CXOs · 2026
          </div>

          {/* Floating KPI tiles */}
          <div className="space-y-3 self-end max-w-xs">
            <div className="backdrop-blur-md bg-white/[0.06] border border-white/10 rounded-lg p-4 animate-fade-up">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-slate-300 font-mono">
                <TrendingUp size={11} className="text-emerald-400" /> Live Portfolio
              </div>
              <div className="mt-1.5 font-mono font-bold text-2xl text-white">₹957.25 Cr</div>
              <div className="text-[11px] text-slate-400 mt-0.5 font-mono">Net Sales · FY 2025-26</div>
            </div>
            <div className="backdrop-blur-md bg-white/[0.06] border border-white/10 rounded-lg p-4 animate-fade-up" style={{ animationDelay: "0.15s" }}>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-slate-300 font-mono">
                <Building2 size={11} className="text-amber-400" /> Active Projects
              </div>
              <div className="mt-1.5 font-mono font-bold text-2xl text-white">18</div>
              <div className="text-[11px] text-slate-400 mt-0.5 font-mono">Across Chennai · Bangalore · Madurai</div>
            </div>
          </div>

          {/* Testimonial */}
          <div className="relative max-w-md">
            <div className="text-3xl font-bold tracking-tight font-display text-white leading-tight">
              <span className="text-amber-400">"</span>Vexen replaced our six-hour monthly review with a six-minute decision.<span className="text-amber-400">"</span>
            </div>
            <div className="text-xs text-slate-400 mt-4 font-mono uppercase tracking-[0.15em]">
              — Director of Operations · Tier-1 Developer · Chennai
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
